import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { abrirArchivo } from "../../../../components/enrollment/visorArchivo";
import { PortalApiPersonal } from "../../../../services/v2/classPortal.service";
import {
	ApiContenido, ArchivoPieza, ContenidoDelNivel, CriterioRubrica, DatosPieza, EntregaPieza, NivelContenido, PiezaContenido, TipoPieza
} from "../../../../services/v2/lmsContenido.service";
import { apiQuizCatalogo } from "../../../../services/v2/lmsQuiz.service";
import { Aviso, exito, Icono, LineaAviso, mensajeDeError, subirArchivo, subirConBoleto, tamanoLegible, useAlVolver } from "../comun";
import PanelPreguntas from "../quiz/panelPreguntas";
import "../portalClase.css";
import "./contenido.css";

/*
	Pestaña Contenido (Portal de Clase, Parte A2, 2026-10-17): el contenido del curso que heredan todos sus horarios.
	Se arma una vez en el curso; el módulo y el horario pueden cambiarlo (override) y volver a lo heredado. Cada pieza dice en qué
	SESIÓN se publica y en cuál vence (no fechas fijas): en cada horario esas sesiones se vuelven fechas reales.
	Un mismo componente sirve para curso, módulo y horario (cambia solo el api).
	Parte D: un quiz del curso o del módulo tiene sus preguntas (botón Preguntas); sus puntos salen de la suma de las preguntas.
*/

export const TIPO_TEXTO: Record<TipoPieza, string> = { unidad: "Unidad", tarea: "Tarea", material: "Material", quiz: "Quiz", pendiente: "Pendiente", anuncio: "Anuncio" }
export const ENTREGA_TEXTO: Record<EntregaPieza, string> = { foto: "Foto", archivo: "Archivo", en_clase: "En Clase", sin_entrega: "Sin Entrega" }
const NUEVOS: TipoPieza[] = ["unidad", "tarea", "material", "quiz", "pendiente", "anuncio"]

/** "19:30" → "7:30 pm" */
export const horaLegible = (hhmm: string | null): string => {
	if (!hhmm) return ""
	const [h, m] = hhmm.split(":").map(Number)
	return (h % 12 || 12) + ":" + String(m).padStart(2, "0") + " " + (h < 12 ? "am" : "pm")
}

const iconoDe = (t: TipoPieza): "doc" | "megafono" | "check" | "casa" | "clip" | "pregunta" =>
	t === "material" ? "doc" : t === "anuncio" ? "megafono" : t === "pendiente" ? "check" : t === "unidad" ? "casa" : t === "quiz" ? "pregunta" : "clip"

const numero = (v: string): number | null => (v.trim() === "" ? null : Number(v))
const dec = (n: number | null | undefined): string => (n === null || n === undefined ? "" : String(n))
const fmt = (n: number): string => String(Math.round(n * 100) / 100)

// ---------------------------------------------------------------- texto de cuándo

export const cuandoPublica = (p: PiezaContenido, nivel: NivelContenido): string => {
	if (p.type === "unidad") {
		const a = p.publishSession ? "Empieza en la sesión " + p.publishSession : "Sin sesión de inicio"
		return a + (p.dueSession ? " · termina en la sesión " + p.dueSession : "")
	}
	if (nivel === "horario") {
		if (p.publishPending) return "Su sesión " + p.publishSession + " todavía no existe"
		return p.publishLabel ? "Se publica " + p.publishLabel : "Se publica de inmediato"
	}
	return p.publishSession ? "Se publica en la sesión " + p.publishSession + (p.publishTime ? " a las " + horaLegible(p.publishTime) : "") : "Se publica de inmediato"
}

export const cuandoVence = (p: PiezaContenido, nivel: NivelContenido): string | null => {
	if (p.type === "unidad" || !p.dueSession) return null
	if (nivel === "horario") return p.dueLabel ? "Vence " + p.dueLabel : "Vence en la sesión " + p.dueSession + " (aún sin fecha)"
	return "Vence en la sesión " + p.dueSession + (p.dueBeforeClass ? ", antes de la clase" : p.dueTime ? " a las " + horaLegible(p.dueTime) : "")
}

// ---------------------------------------------------------------- formulario de una pieza

interface ArchivoForm { key?: string; link?: string; name: string; mime?: string | null; size?: number }

const archivoDeForm = (a: ArchivoPieza): ArchivoForm => (a.kind === "enlace" ? { link: a.url || "", name: a.name } : { key: a.key || "", name: a.name, mime: a.mime, size: a.size })

interface PropsForm
{
	api: ApiContenido
	datos: ContenidoDelNivel
	tipo: TipoPieza
	inicial: PiezaContenido | null
	onGuardado: (d: ContenidoDelNivel) => void
	onCancelar: () => void
}

const FormPieza: React.FC<PropsForm> = ({ api, datos, tipo, inicial, onGuardado, onCancelar }) => {
	const esOverride = !!inicial && !inicial.isOwn
	const [titulo, setTitulo] = useState<string>(inicial?.title || "")
	const [cuerpo, setCuerpo] = useState<string>(inicial?.body || "")
	const [unidad, setUnidad] = useState<string>(inicial?.unitItemId ? String(inicial.unitItemId) : "")
	const [pubSesion, setPubSesion] = useState<string>(dec(inicial?.publishSession))
	const [pubHora, setPubHora] = useState<string>(inicial?.publishTime || "")
	const [venSesion, setVenSesion] = useState<string>(dec(inicial?.dueSession))
	const [venModo, setVenModo] = useState<"clase" | "hora" | "dia">(inicial?.dueBeforeClass ? "clase" : inicial?.dueTime ? "hora" : "dia")
	const [venHora, setVenHora] = useState<string>(inicial?.dueTime || "")
	const [entrega, setEntrega] = useState<EntregaPieza>(inicial?.delivery || "archivo")
	const [puntos, setPuntos] = useState<string>(inicial ? dec(inicial.points) : tipo === "tarea" || tipo === "quiz" ? "100" : "")
	const [categoria, setCategoria] = useState<string>(inicial?.category || "")
	const [pesoFijo, setPesoFijo] = useState<string>(dec(inicial?.fixedWeightPct))
	const [acepta, setAcepta] = useState<boolean>(inicial ? inicial.acceptLate : true)
	const [penal, setPenal] = useState<string>(inicial ? dec(inicial.latePenaltyPct) : "0")
	const [rubrica, setRubrica] = useState<CriterioRubrica[]>(inicial?.rubric || [])
	const [archivos, setArchivos] = useState<ArchivoForm[]>(inicial ? inicial.files.map(archivoDeForm) : [])
	const [enlace, setEnlace] = useState<string>("")
	const [subiendo, setSubiendo] = useState<{ nombre: string; porciento: number } | null>(null)
	const [guardando, setGuardando] = useState<boolean>(false)
	const [error, setError] = useState<string | null>(null)
	const entrada = useRef<HTMLInputElement>(null)

	const esTarea = tipo === "tarea"
	const conNota = tipo === "tarea" || tipo === "quiz"
	const conVence = conNota || tipo === "pendiente"
	const conArchivos = tipo === "material" || tipo === "tarea"
	const conPublica = tipo !== "unidad"
	const sumaRubrica = rubrica.reduce((s, c) => s + (Number(c.points) || 0), 0)
	const categorias = datos.scheme.categories.filter((c) => !c.esAsistencia)
	const unidadesDisponibles = datos.units.filter((u) => !u.hidden)
	const nivelTexto = api.nivel === "horario" ? "este horario" : "este módulo"

	const adaptador = useMemo(() => ({ uploadUrl: (d: { name: string; size: number; mime: string }) => api.subida(d) } as unknown as PortalApiPersonal), [api])
	const agregarArchivos = async (lista: FileList | null) => {
		if (!lista) return
		setError(null)
		for (const f of Array.from(lista)) {
			if (archivos.length >= 10) { setError("Máximo 10 archivos por pieza."); break }
			if (f.size > 200 * 1024 * 1024) { setError("\"" + f.name + "\" pesa más de 200 MB."); continue }
			try {
				setSubiendo({ nombre: f.name, porciento: 0 })
				const key = await subirArchivo(adaptador, f, "adjunto", (p) => setSubiendo({ nombre: f.name, porciento: p }))
				setArchivos((a) => [...a, { key, name: f.name, mime: f.type || null, size: f.size }])
			} catch (e) { setError(mensajeDeError(e, "No se pudo subir \"" + f.name + "\".")) }
		}
		setSubiendo(null)
		if (entrada.current) entrada.current.value = ""
	}
	const agregarEnlace = () => {
		const u = enlace.trim()
		if (!/^https?:\/\/\S+$/i.test(u)) { setError("El enlace debe empezar con http:// o https://."); return }
		let nombre = u
		try { nombre = new URL(u).hostname.replace(/^www\./, "") } catch { /* se queda la dirección */ }
		setArchivos((a) => [...a, { link: u, name: nombre }]); setEnlace(""); setError(null)
	}

	const guardar = async () => {
		if (guardando || subiendo) return
		setError(null)
		if (tipo !== "anuncio" && titulo.trim() === "") { setError("Ponle título a la pieza."); return }
		if (tipo === "anuncio" && titulo.trim() === "" && cuerpo.trim() === "") { setError("Escribe algo en el anuncio."); return }
		const d: DatosPieza = { title: titulo.trim(), body: cuerpo.trim() }
		if (tipo !== "unidad") d.unitItemId = unidad ? Number(unidad) : null
		d.publishSession = numero(pubSesion)
		if (conPublica) d.publishTime = pubSesion.trim() === "" ? null : pubHora || null
		if (tipo === "unidad" || conVence) {
			d.dueSession = numero(venSesion)
			if (conVence) { d.dueBeforeClass = venSesion.trim() !== "" && venModo === "clase"; d.dueTime = venSesion.trim() !== "" && venModo === "hora" ? venHora || null : null }
		}
		if (esTarea) d.delivery = entrega
		if (conNota) {
			if (tipo !== "quiz") d.points = rubrica.length > 0 && esTarea ? Math.round(sumaRubrica * 100) / 100 : numero(puntos)
			d.category = categoria || null
			d.fixedWeightPct = numero(pesoFijo)
			d.acceptLate = acepta
			d.latePenaltyPct = acepta ? numero(penal) ?? 0 : 0
		}
		if (esTarea) d.rubric = rubrica.map((c) => ({ name: c.name.trim(), points: Number(c.points) }))
		if (conArchivos) d.files = archivos.map((a) => (a.link ? { link: a.link, name: a.name } : { key: a.key || "", name: a.name, mime: a.mime, size: a.size }))
		setGuardando(true)
		try {
			const r = inicial ? await api.cambiar(inicial.id, d) : await api.crear({ ...d, type: tipo } as DatosPieza & { type: TipoPieza })
			onGuardado(r)
		} catch (e) { setError(mensajeDeError(e)) } finally { setGuardando(false) }
	}

	const campoNum = (id: string, etiqueta: string, valor: string, poner: (v: string) => void, extra?: { min?: number; max?: number; step?: number; corto?: boolean; ayuda?: string; deshabilitado?: boolean }) => (
		<div className={"pcCampo" + (extra?.corto ? " pcCorto" : "")}>
			<label className="pcCampoEtiqueta" htmlFor={id}>{etiqueta}</label>
			<input id={id} type="number" inputMode="decimal" className="pcContInput" value={valor} min={extra?.min} max={extra?.max} step={extra?.step || 1}
				disabled={extra?.deshabilitado} onChange={(e) => poner(e.target.value)} />
			{extra?.ayuda ? <span className="pcAyuda">{extra.ayuda}</span> : null}
		</div>
	)

	return (
		<div className="pcForm">
			<h3 className="pcFormTitulo">{inicial ? (esOverride ? "Cambiar " : "Editar ") : "Nueva "}{TIPO_TEXTO[tipo]}</h3>
			{esOverride ? <div className="pcNota pcNotaOro">Solo cambias esto en {nivelTexto}. Lo demás sigue al {inicial?.origin === "modulo" ? "módulo" : "curso"}; puedes volver a lo heredado cuando quieras.</div> : null}
			<div className="pcCampo">
				<label className="pcCampoEtiqueta" htmlFor="fpTitulo">{tipo === "anuncio" ? "Título (Opcional)" : "Título"}</label>
				<input id="fpTitulo" className="pcContInput" value={titulo} maxLength={200} onChange={(e) => setTitulo(e.target.value)} />
			</div>
			{tipo !== "unidad" ? (
				<div className="pcCampo">
					<label className="pcCampoEtiqueta" htmlFor="fpCuerpo">{tipo === "anuncio" ? "Anuncio" : "Descripción"}</label>
					<textarea id="fpCuerpo" className="pcTextarea" rows={3} maxLength={5000} value={cuerpo} onChange={(e) => setCuerpo(e.target.value)} />
				</div>
			) : null}
			{tipo !== "unidad" && unidadesDisponibles.length > 0 ? (
				<div className="pcCampo">
					<label className="pcCampoEtiqueta" htmlFor="fpUnidad">Unidad</label>
					<select id="fpUnidad" className="pcSelect" value={unidad} onChange={(e) => setUnidad(e.target.value)}>
						<option value="">Sin Unidad</option>
						{unidadesDisponibles.map((u) => <option key={u.id} value={u.id}>{u.title}</option>)}
					</select>
				</div>
			) : null}

			<div className="pcFormFila">
				{campoNum("fpPub", tipo === "unidad" ? "Empieza En La Sesión" : "Se Publica En La Sesión", pubSesion, setPubSesion, { min: 1, max: 400, ayuda: "Vacío = de inmediato" })}
				{conPublica && pubSesion.trim() !== "" ? (
					<div className="pcCampo">
						<label className="pcCampoEtiqueta" htmlFor="fpPubHora">A La Hora</label>
						<input id="fpPubHora" type="time" className="pcContInput" value={pubHora} onChange={(e) => setPubHora(e.target.value)} />
						<span className="pcAyuda">Vacío = al empezar la clase</span>
					</div>
				) : null}
				{tipo === "unidad" ? campoNum("fpVen", "Termina En La Sesión", venSesion, setVenSesion, { min: 1, max: 400 }) : null}
				{conVence ? campoNum("fpVen", "Vence En La Sesión", venSesion, setVenSesion, { min: 1, max: 400, ayuda: "Vacío = sin vencimiento" }) : null}
			</div>
			{datos.sessionsMax > 0 && [pubSesion, venSesion].some((v) => v.trim() !== "" && Number(v) > datos.sessionsMax) ? (
				<div className="pcNota pcNotaOro">{datos.level === "horario" ? "Esta clase tiene " + datos.sessionsMax + " sesiones" : "Los horarios tienen hasta " + datos.sessionsMax + " sesiones"}: en una sesión que no existe la pieza no se publica o queda sin fecha de entrega.</div>
			) : null}
			{conVence && venSesion.trim() !== "" ? (
				<div className="pcCampo">
					<span className="pcCampoEtiqueta">Cuándo Vence Ese Día</span>
					<div className="pcRadios" role="radiogroup" aria-label="Cuándo vence ese día">
						<label><input type="radio" name="venModo" checked={venModo === "clase"} onChange={() => setVenModo("clase")} />Antes De La Clase</label>
						<label><input type="radio" name="venModo" checked={venModo === "hora"} onChange={() => setVenModo("hora")} />A Una Hora</label>
						<label><input type="radio" name="venModo" checked={venModo === "dia"} onChange={() => setVenModo("dia")} />Al Terminar El Día</label>
					</div>
					{venModo === "hora" ? <input type="time" aria-label="Hora de vencimiento" className="pcContInput" style={{ maxWidth: 160 }} value={venHora} onChange={(e) => setVenHora(e.target.value)} /> : null}
				</div>
			) : null}

			{esTarea ? (
				<div className="pcCampo">
					<label className="pcCampoEtiqueta" htmlFor="fpEntrega">Forma De Entrega</label>
					<select id="fpEntrega" className="pcSelect" value={entrega} onChange={(e) => setEntrega(e.target.value as EntregaPieza)}>
						{datos.deliveries.map((x) => <option key={x} value={x}>{ENTREGA_TEXTO[x]}</option>)}
					</select>
				</div>
			) : null}
			{conNota ? (
				<>
					{tipo === "quiz" ? <div className="pcNota">Los puntos del quiz son la suma de sus preguntas. {inicial ? "Las preguntas se cambian con el botón Preguntas." : "Al crearlo se abren sus preguntas."}</div> : null}
					<div className="pcFormFila">
						{tipo === "quiz" ? null : campoNum("fpPuntos", "Puntos", rubrica.length > 0 && esTarea ? fmt(sumaRubrica) : puntos, setPuntos, { min: 0.01, max: 1000, step: 0.01, deshabilitado: rubrica.length > 0 && esTarea, ayuda: rubrica.length > 0 && esTarea ? "Suma de la rúbrica" : undefined })}
						<div className="pcCampo">
							<label className="pcCampoEtiqueta" htmlFor="fpCategoria">Categoría</label>
							<select id="fpCategoria" className="pcSelect" value={categoria} onChange={(e) => setCategoria(e.target.value)}>
								<option value="">Sin Categoría</option>
								{categorias.map((c) => <option key={c.name} value={c.name}>{c.name} ({fmt(c.weight)} %)</option>)}
								{categoria && !categorias.some((c) => c.name.toLowerCase() === categoria.toLowerCase()) ? <option value={categoria}>{categoria} (ya no existe)</option> : null}
							</select>
						</div>
						{campoNum("fpFijo", "Peso Fijo (%)", pesoFijo, setPesoFijo, { min: 0.01, max: 100, step: 0.01, ayuda: "Opcional" })}
					</div>
					<div className="pcChecks">
						<label><input type="checkbox" checked={acepta} onChange={(e) => setAcepta(e.target.checked)} />Acepta Entregas Tarde</label>
					</div>
					{acepta ? campoNum("fpPenal", "Descuento Por Día Tarde (%)", penal, setPenal, { min: 0, max: 100, step: 0.5, corto: true }) : null}
				</>
			) : null}
			{esTarea ? (
				<div className="pcCampo">
					<span className="pcCampoEtiqueta">Rúbrica (Opcional)</span>
					{rubrica.map((c, i) => (
						<div className="pcRubricaFila" key={i}>
							<input className="pcContInput" aria-label={"Criterio " + (i + 1)} placeholder="Criterio" value={c.name} maxLength={160} onChange={(e) => setRubrica(rubrica.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
							<input className="pcContInput pcPuntos" type="number" inputMode="decimal" aria-label={"Puntos del criterio " + (i + 1)} placeholder="Pts" min={0} step={0.01} value={c.points === 0 ? "" : c.points} onChange={(e) => setRubrica(rubrica.map((x, j) => (j === i ? { ...x, points: Number(e.target.value) } : x)))} />
							<button type="button" className="pcIb" style={{ width: 40, height: 40 }} aria-label={"Quitar criterio " + (i + 1)} onClick={() => setRubrica(rubrica.filter((_, j) => j !== i))}><Icono n="cerrar" size={16} /></button>
						</div>
					))}
					<div className="pcRedactorBotones">
						<button type="button" className="pcBtn pcBtnS pcBtnChico" disabled={rubrica.length >= 12} onClick={() => setRubrica([...rubrica, { name: "", points: 0 }])}>Agregar Criterio</button>
						{rubrica.length > 0 ? <span className="pcAyuda" style={{ alignSelf: "center" }}>La tarea vale {fmt(sumaRubrica)} puntos</span> : null}
					</div>
				</div>
			) : null}
			{conArchivos ? (
				<div className="pcCampo">
					<span className="pcCampoEtiqueta">{tipo === "material" ? "Archivos Y Enlaces" : "Archivos De Apoyo (Opcional)"}</span>
					{archivos.map((a, i) => (
						<div className="pcAdjuntoFila" key={(a.key || a.link) + "-" + i}>
							<Icono n={a.link ? "enlace" : "doc"} /><span className="pcNombre">{a.name}</span>{a.size ? <small className="pcTenue">{tamanoLegible(a.size)}</small> : null}
							<button type="button" className="pcIb" style={{ width: 36, height: 36 }} aria-label={"Quitar " + a.name} onClick={() => setArchivos(archivos.filter((_, j) => j !== i))}><Icono n="cerrar" size={16} /></button>
						</div>
					))}
					{subiendo ? <div><div className="pcChico pcTenue">Subiendo {subiendo.nombre} {subiendo.porciento}%</div><div className="pcBarra"><div style={{ width: subiendo.porciento + "%" }} /></div></div> : null}
					<div className="pcRedactorBotones">
						<input ref={entrada} type="file" multiple hidden onChange={(e) => agregarArchivos(e.target.files)} />
						<button type="button" className="pcBtn pcBtnS pcBtnChico" disabled={!!subiendo} onClick={() => entrada.current?.click()}><Icono n="clip" />Adjuntar Archivo</button>
						<input className="pcContInput" style={{ flex: "1 1 200px", height: 40 }} aria-label="Enlace" placeholder="Pegar Enlace" value={enlace} onChange={(e) => setEnlace(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); agregarEnlace() } }} />
						<button type="button" className="pcBtn pcBtnS pcBtnChico" disabled={enlace.trim() === ""} onClick={agregarEnlace}><Icono n="enlace" />Agregar Enlace</button>
					</div>
				</div>
			) : null}
			{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
			<div className="pcRedactorPie"><div className="pcDerecha">
				<button type="button" className="pcBtn pcBtnS" onClick={onCancelar}>Cancelar</button>
				<button type="button" className="pcBtn pcBtnP" disabled={guardando || !!subiendo} onClick={guardar}>Guardar</button>
			</div></div>
		</div>
	)
}

// ---------------------------------------------------------------- una pieza en la lista

interface PropsFila
{
	api: ApiContenido
	datos: ContenidoDelNivel
	p: PiezaContenido
	nivel: NivelContenido
	onEditar: () => void
	/** Parte D: abre las preguntas de un quiz (solo curso y módulo). */
	onPreguntas?: () => void
	onAccion: (f: () => Promise<ContenidoDelNivel>, confirmar?: string) => void
	mover?: { arriba: (() => void) | null; abajo: (() => void) | null }
}

const FilaPieza: React.FC<PropsFila> = ({ api, datos, p, nivel, onEditar, onPreguntas, onAccion, mover }) => {
	const puede = datos.canEdit
	const esHeredada = !p.isOwn
	const alDe = nivel === "horario" && (p.changedAtModule || p.origin === "modulo") ? "Módulo" : "Curso"
	const vence = cuandoVence(p, nivel)
	const abrir = async (a: ArchivoPieza, pos: number) => {
		if (a.kind === "enlace") { window.open(a.url || "#", "_blank", "noopener,noreferrer"); return }
		const url = async () => (await api.archivo(p.id, pos)).url
		abrirArchivo({ nombre: a.name, mime: a.mime, origen: "Contenido", obtenerUrl: url, cargar: async () => { const r = await fetch(await url()); if (!r.ok) throw new Error("No se pudo leer el archivo"); return { blob: await r.blob(), nombre: a.name } } })
	}
	return (
		<article className={"pcPieza" + (p.hidden ? " pcPiezaOculta" : "")}>
			<div className="pcPiezaFila">
				<span className={"pcPiezaIcono" + (p.type === "tarea" || p.type === "quiz" ? " pcTarea" : "")} aria-hidden="true"><Icono n={iconoDe(p.type)} size={20} /></span>
				<div className="pcPiezaTextos">
					<div className="pcPiezaChips">
						<span className="pcChip pcChipBorde">{TIPO_TEXTO[p.type]}</span>
						{nivel !== "curso" && !p.retired ? <span className="pcChip pcChipBorde">{p.originLabel}</span> : null}
						{p.changedHere ? <span className="pcChip pcChipAcento">Cambiada Aquí</span> : null}
						{p.hidden ? <span className="pcChip pcChipOro">Oculta</span> : null}
						{p.hiddenAbove ? <span className="pcChip pcChipOro">Oculta Por El Módulo</span> : null}
						{p.retired ? <span className="pcChip pcChipOro">Retirada Del Curso</span> : null}
						{p.type === "quiz" && p.noQuestions ? <span className="pcChip pcChipOro">Sin Preguntas · No Se Publica</span> : null}
					</div>
					<div className="pcPiezaTitulo">{p.title || (p.body ? p.body.slice(0, 80) : "Sin Título")}</div>
					{p.retired ? <div className="pcPiezaMeta">Ya tiene entregas, por eso se queda y sigue contando hasta que la ocultes.</div> : (
						<>
							<div className="pcPiezaMeta">{cuandoPublica(p, nivel)}{vence ? " · " + vence : ""}</div>
							{p.type === "tarea" || p.type === "quiz" ? (
								<div className="pcTareaDatos">
									{p.type === "quiz" && p.noQuestions ? null : <span className="pcChip pcChipAcento">{p.points !== null ? fmt(p.points) + " Pts" : "Sin Puntos"}</span>}
									{p.category ? (datos.scheme.categories.some((c) => c.name.toLowerCase() === p.category!.toLowerCase())
										? <span className="pcChip pcChipBorde">{p.category}</span> : <span className="pcChip pcChipOro">Sin Categoría</span>)
										: <span className="pcChip pcChipOro">Sin Categoría</span>}
									{p.type === "tarea" && p.delivery ? <span className="pcChip pcChipBorde">{ENTREGA_TEXTO[p.delivery]}</span> : null}
									{p.fixedWeightPct !== null ? <span className="pcChip pcChipBorde">Peso Fijo {fmt(p.fixedWeightPct)} %</span> : null}
									{p.rubric.length > 0 ? <span className="pcChip pcChipBorde">Rúbrica De {p.rubric.length}</span> : null}
								</div>
							) : null}
							{p.body ? <div className="pcPiezaMeta" style={{ color: "var(--pc-medio)" }}>{p.body.length > 140 ? p.body.slice(0, 140) + "…" : p.body}</div> : null}
							{p.files.length > 0 ? (
								<div className="pcArchivos">
									{p.files.map((a, i) => (
										<button type="button" className="pcArchivo" key={i} onClick={() => abrir(a, i)}><Icono n={a.kind === "enlace" ? "enlace" : "doc"} /><span className="pcNombre">{a.name}</span></button>
									))}
								</div>
							) : null}
						</>
					)}
				</div>
			</div>
			{puede ? (
				<div className="pcPiezaAcciones">
					{p.retired ? (
						<button type="button" onClick={() => onAccion(() => api.ocultar(p.id, true, true), "¿Ocultar \"" + p.title + "\" en esta clase? Las entregas y notas se conservan.")}>Ocultar</button>
					) : (
						<>
							{!p.hiddenAbove ? <button type="button" onClick={onEditar}>{esHeredada ? "Cambiar Aquí" : "Editar"}</button> : null}
							{onPreguntas ? <button type="button" onClick={onPreguntas}>{esHeredada ? "Ver Preguntas" : "Preguntas"}</button> : null}
							{esHeredada && nivel !== "curso" ? (
								p.hidden
									? <button type="button" onClick={() => onAccion(() => api.ocultar(p.id, false))}>Mostrar</button>
									: <button type="button" onClick={() => onAccion(() => api.ocultar(p.id, true))}>Ocultar</button>
							) : null}
							{esHeredada && p.changedHere ? <button type="button" onClick={() => onAccion(() => api.revertir(p.id))}>Volver A Lo Del {alDe}</button> : null}
							{!esHeredada && nivel !== "horario" ? <button type="button" onClick={() => onAccion(() => api.borrar(p.id), "¿Borrar \"" + (p.title || "esta pieza") + "\"? Los horarios dejan de recibirla; las entregas que ya existan se conservan.")}>Borrar</button> : null}
							{mover ? (
								<span className="pcFlechas">
									<button type="button" aria-label="Subir" disabled={!mover.arriba} onClick={() => mover.arriba && mover.arriba()}>↑</button>
									<button type="button" aria-label="Bajar" disabled={!mover.abajo} onClick={() => mover.abajo && mover.abajo()}>↓</button>
								</span>
							) : null}
						</>
					)}
				</div>
			) : null}
		</article>
	)
}

// ---------------------------------------------------------------- panel

export const PanelContenido: React.FC<{ api: ApiContenido; onCambio?: () => void }> = ({ api, onCambio }) => {
	const [datos, setDatos] = useState<ContenidoDelNivel | null>(null)
	const [error, setError] = useState<string | null>(null)
	const [aviso, setAviso] = useState<Aviso | null>(null)
	const [form, setForm] = useState<{ tipo: TipoPieza; id: number | null } | null>(null)
	const [preguntas, setPreguntas] = useState<number | null>(null)
	const apiQuiz = React.useMemo(() => (api.nivel === "horario" ? null : apiQuizCatalogo(api.nivel, api.id)), [api])
	const [menuNuevo, setMenuNuevo] = useState<boolean>(false)
	const pedido = useRef<number>(0)

	const cargar = useCallback(() => {
		const n = ++pedido.current
		api.contenido().then((d) => { if (n === pedido.current) { setDatos(d); setError(null) } })
			.catch((e) => { if (n === pedido.current) setError(mensajeDeError(e, "No pudimos cargar el contenido.")) })
	}, [api])
	useEffect(() => { setDatos(null); setForm(null); setPreguntas(null); cargar() }, [cargar])
	useAlVolver(() => { if (!form && preguntas === null) cargar() })

	const aplicar = (d: ContenidoDelNivel, texto?: string) => { setDatos(d); setForm(null); setError(null); if (texto) setAviso(exito(texto)); if (onCambio) onCambio() }
	const accion = async (f: () => Promise<ContenidoDelNivel>, confirmar?: string) => {
		if (confirmar && !window.confirm(confirmar)) return
		setAviso(null)
		try { aplicar(await f()) } catch (e) { setError(mensajeDeError(e)); cargar() }
	}

	if (error && !datos) return <div className="pc pcEnFicha"><div className="pcAviso pcOro" role="alert">{error}</div></div>
	if (!datos) return <div className="pc pcEnFicha"><div className="pcCargando">Cargando…</div></div>

	const nivel = api.nivel
	const propias = datos.items.filter((p) => p.isOwn && !p.retired)
	const mover = (p: PiezaContenido, grupo: PiezaContenido[]) => {
		if (!datos.canEdit || !p.isOwn || nivel === "horario") return undefined
		const i = grupo.findIndex((x) => x.id === p.id)
		const mismos = grupo.filter((x) => x.isOwn)
		const j = mismos.findIndex((x) => x.id === p.id)
		const permutar = (delta: number) => () => {
			const otro = mismos[j + delta]
			if (!otro) return
			const orden = propias.map((x) => x.id)
			const a = orden.indexOf(p.id), b = orden.indexOf(otro.id)
			orden[a] = otro.id; orden[b] = p.id
			accion(() => api.ordenar(orden))
		}
		return i < 0 ? undefined : { arriba: j > 0 ? permutar(-1) : null, abajo: j >= 0 && j < mismos.length - 1 ? permutar(1) : null }
	}

	const unidades = datos.items.filter((p) => p.type === "unidad" && !p.retired)
	const sueltas = datos.items.filter((p) => p.type !== "unidad" && !p.retired && (p.unitItemId === null || !unidades.some((u) => u.id === p.unitItemId)))
	const retiradas = datos.items.filter((p) => p.retired)

	const fila = (p: PiezaContenido, grupo: PiezaContenido[]) => (
		form && form.id === p.id
			? <FormPieza key={p.id} api={api} datos={datos} tipo={p.type} inicial={p} onGuardado={(d) => aplicar(d, "Guardado")} onCancelar={() => setForm(null)} />
			: preguntas === p.id && apiQuiz
				? <PanelPreguntas key={p.id} titulo={p.title || "Quiz"} cargar={() => apiQuiz.get(p.id)} guardar={(d) => apiQuiz.save(p.id, d)}
					subirImagen={(f) => subirConBoleto((tipo) => api.subida({ name: f.name, size: f.size, mime: tipo }), f)}
					onCerrar={(g) => { setPreguntas(null); if (g) { cargar(); setAviso(exito("Preguntas Guardadas")); if (onCambio) onCambio() } }} />
				: <FilaPieza key={p.id} api={api} datos={datos} p={p} nivel={nivel} onEditar={() => { setForm({ tipo: p.type, id: p.id }); setPreguntas(null); setAviso(null) }} onAccion={accion} mover={mover(p, grupo)}
					onPreguntas={p.type === "quiz" && apiQuiz && !p.retired ? () => { setPreguntas(p.id); setForm(null); setAviso(null) } : undefined} />
	)

	const alcance = nivel === "horario" ? null : datos.schedules === 1 ? "Lo reciben 1 horario vigente" : "Lo reciben " + datos.schedules + " horarios vigentes"
	return (
		<div className="pc pcEnFicha">
			<div className="pcCont">
				<div className="pcContCabeza">
					<div className="pcContTitulo">Contenido {nivel === "curso" ? "Del Curso" : nivel === "modulo" ? "Del Módulo" : "De La Clase"}</div>
					{datos.canEdit && nivel !== "horario" ? (
						<div className="pcMenu">
							<button type="button" className="pcBtn pcBtnP pcBtnChico" aria-expanded={menuNuevo} onClick={() => setMenuNuevo(!menuNuevo)}>Nueva Pieza</button>
							{menuNuevo ? (
								<div className="pcMenuLista" role="menu">
									{NUEVOS.map((t) => <button key={t} type="button" role="menuitem" onClick={() => { setMenuNuevo(false); setForm({ tipo: t, id: null }); setAviso(null) }}>{TIPO_TEXTO[t]}</button>)}
								</div>
							) : null}
						</div>
					) : null}
				</div>
				<div className="pcPiezaMeta">
					{nivel === "horario"
						? "Lo que viene del curso o del módulo se publica solo con las sesiones de esta clase. Tus anuncios, materiales y pendientes propios se agregan desde el Muro."
						: "Cada pieza se publica y vence por número de sesión; en cada horario se vuelven fechas reales y, si un asueto recorre las sesiones, las fechas se recorren solas."}
					{alcance ? " " + alcance + (datos.sessionsMax ? " (hasta " + datos.sessionsMax + " sesiones)." : ".") : ""}
				</div>
				{nivel === "horario" && !datos.canEdit ? <div className="pcNota">Lo del curso lo cambia Servicios Académicos. Tú puedes agregar tus propias piezas y anuncios.</div> : null}
				<LineaAviso a={aviso} />
				{error ? <div className="pcAviso pcOro" role="alert">{error}</div> : null}
				{datos.errors.length > 0 || datos.warnings.length > 0 ? (
					<div className="pcContAvisos" role="status">
						{datos.errors.map((m, i) => <div className="pcNota pcNotaOro" key={"e" + i}>{m}</div>)}
						{datos.warnings.map((w, i) => <div className="pcNota pcNotaOro" key={"w" + i}>{w.message}</div>)}
					</div>
				) : null}

				{form && form.id === null ? <FormPieza api={api} datos={datos} tipo={form.tipo} inicial={null} onCancelar={() => setForm(null)} onGuardado={(d) => {
					const antes = new Set(datos.items.map((x) => x.id))
					const nueva = d.items.find((x) => !antes.has(x.id) && x.type === "quiz")
					aplicar(d, "Pieza Creada")
					// Un quiz recién creado abre sus preguntas.
					if (nueva && apiQuiz) setPreguntas(nueva.id)
				}} /> : null}
				{datos.items.length === 0 && !form ? <div className="pcVacio">{datos.canEdit && nivel !== "horario" ? "Aún No Hay Piezas: Crea La Primera Con Nueva Pieza." : "Aún No Hay Contenido Del Curso."}</div> : null}

				{unidades.map((u) => {
					const hijos = datos.items.filter((p) => p.unitItemId === u.id && p.type !== "unidad" && !p.retired)
					return (
						<section className="pcContUnidad" key={u.id} aria-label={u.title || "Unidad"}>
							{fila(u, unidades)}
							{hijos.length > 0 ? <div className="pcContGrupo">{hijos.map((p) => fila(p, hijos))}</div> : null}
						</section>
					)
				})}
				{sueltas.length > 0 ? (
					<section className="pcContUnidad" aria-label="Sin unidad">
						{unidades.length > 0 ? <div className="pcSeccion">Sin Unidad</div> : null}
						{sueltas.map((p) => fila(p, sueltas))}
					</section>
				) : null}
				{retiradas.length > 0 ? (
					<section className="pcContUnidad" aria-label="Retiradas del curso">
						<div className="pcSeccion">Retiradas Del Curso</div>
						{retiradas.map((p) => fila(p, retiradas))}
					</section>
				) : null}
			</div>
		</div>
	)
}

export default PanelContenido
